/**
 * publisher.ts
 * Omnichannel publishing broker — transforms GeneratedBundle payloads
 * into platform-specific API request structures and dispatches them.
 *
 * Supported platforms:
 *   X (Twitter)   — POST /2/tweets
 *   LinkedIn      — POST /v2/ugcPosts
 *   WordPress     — POST /wp-json/wp/v2/posts
 *   Instagram     — Meta Graph /media + /media_publish
 *   Facebook      — Meta Graph /feed
 *   YouTube       — YouTube Data v3 /videos:insert (metadata)
 *   MGB           — Enterprise Blog strict JSON contract
 */

import type { GeneratedBundle } from './openai';
import { trackEvent, createCampaign, updateCampaignStatus } from './analytics';

// ── Types ─────────────────────────────────────────────────────────────────────

export type PlatformId =
  | 'X'
  | 'LinkedIn'
  | 'WordPress'
  | 'Instagram'
  | 'Facebook'
  | 'YouTube'
  | 'MGB';

export interface PlatformConfig {
  enabled:        boolean;
  token:          string;
  // platform-specific fields
  wpBaseUrl?:     string;   // WordPress
  pageId?:        string;   // Facebook / Instagram
  igUserId?:      string;   // Instagram
  ytChannelId?:   string;   // YouTube (metadata only)
  mgbBaseUrl?:    string;   // MGB
  mgbAuthToken?:  string;   // MGB compliance token
  linkedInUrn?:   string;   // LinkedIn author URN  e.g. urn:li:person:xxxx
}

export type PlatformConfigs = Record<PlatformId, PlatformConfig>;

export interface PublishResult {
  platform:   PlatformId;
  success:    boolean;
  statusCode: number;
  message:    string;
  responseId?: string;
}

// ── Default empty configs ─────────────────────────────────────────────────────

export const DEFAULT_PLATFORM_CONFIGS: PlatformConfigs = {
  X:         { enabled: false, token: '' },
  LinkedIn:  { enabled: false, token: '', linkedInUrn: '' },
  WordPress: { enabled: false, token: '', wpBaseUrl: '' },
  Instagram: { enabled: false, token: '', igUserId: '' },
  Facebook:  { enabled: false, token: '', pageId: '' },
  YouTube:   { enabled: false, token: '', ytChannelId: '' },
  MGB:       { enabled: false, token: '', mgbBaseUrl: '', mgbAuthToken: '' },
};

const CONFIGS_STORAGE_KEY = 'uxcf_platform_configs';

export function loadPlatformConfigs(): PlatformConfigs {
  try {
    const raw = localStorage.getItem(CONFIGS_STORAGE_KEY);
    if (raw) return { ...DEFAULT_PLATFORM_CONFIGS, ...JSON.parse(raw) } as PlatformConfigs;
  } catch { /* ignore */ }
  return { ...DEFAULT_PLATFORM_CONFIGS };
}

export function savePlatformConfigs(configs: PlatformConfigs): void {
  localStorage.setItem(CONFIGS_STORAGE_KEY, JSON.stringify(configs));
}

// ── Payload transformers ──────────────────────────────────────────────────────

function buildXPayload(bundle: GeneratedBundle): Record<string, unknown> {
  // Use the first X thread tweet or blog excerpt as tweet text (≤280 chars)
  const text =
    bundle.xThread?.tweets?.[0] ??
    `${bundle.blogPost.title}\n\n${bundle.blogPost.excerpt}`.slice(0, 277) + '…';
  return { text: text.slice(0, 280) };
}

function buildLinkedInPayload(bundle: GeneratedBundle, authorUrn: string): Record<string, unknown> {
  const text =
    bundle.linkedInNewsletter?.intro ??
    `${bundle.blogPost.title}\n\n${bundle.blogPost.excerpt}`;
  return {
    author: authorUrn || 'urn:li:person:REPLACE_ME',
    lifecycleState: 'PUBLISHED',
    specificContent: {
      'com.linkedin.ugc.ShareContent': {
        shareCommentary: { text },
        shareMediaCategory: 'NONE',
      },
    },
    visibility: {
      'com.linkedin.ugc.MemberNetworkVisibility': 'PUBLIC',
    },
  };
}

function buildWordPressPayload(bundle: GeneratedBundle): Record<string, unknown> {
  return {
    title:   bundle.blogPost.title,
    content: bundle.blogPost.body,
    excerpt: bundle.blogPost.excerpt,
    status:  'publish',
    tags:    bundle.blogPost.tags?.join(',') ?? '',
  };
}

function buildInstagramPayload(bundle: GeneratedBundle): Record<string, unknown> {
  // Instagram media container — requires image_url from visual prompts or a placeholder
  const caption =
    `${bundle.metaStory?.reelCaption ?? bundle.blogPost.excerpt}\n\n` +
    (bundle.metaStory?.hashtags?.join(' ') ?? bundle.blogPost.tags?.map(t => `#${t}`).join(' ') ?? '');
  return {
    media_type: 'IMAGE',
    caption:    caption.slice(0, 2200),
    // image_url must be a public URL; users fill this after generation
    image_url:  'REPLACE_WITH_IMAGE_URL',
  };
}

function buildFacebookPayload(bundle: GeneratedBundle): Record<string, unknown> {
  const message =
    bundle.metaStory?.storyContent ??
    `${bundle.blogPost.title}\n\n${bundle.blogPost.excerpt}`;
  return {
    message: message.slice(0, 63206),
  };
}

function buildYouTubePayload(bundle: GeneratedBundle): Record<string, unknown> {
  // YouTube v3 video insert body (metadata container — no binary upload here)
  return {
    snippet: {
      title:       bundle.videoWorkflow?.hook ?? bundle.blogPost.title,
      description: bundle.videoWorkflow?.script ?? bundle.blogPost.excerpt,
      tags:        bundle.blogPost.tags ?? [],
      categoryId:  '22', // People & Blogs
    },
    status: {
      privacyStatus: 'public',
    },
  };
}

function buildMGBPayload(
  bundle: GeneratedBundle,
  complianceToken: string,
): Record<string, unknown> {
  return {
    title:               bundle.blogPost.title,
    body_html:           bundle.blogPost.body.replace(/\n/g, '<br/>'),
    excerpt:             bundle.blogPost.excerpt,
    tags:                bundle.blogPost.tags ?? [],
    seo_title:           bundle.blogPost.title,
    seo_description:     bundle.blogPost.excerpt.slice(0, 155),
    compliance_token:    complianceToken,
    structured_payload:  bundle.structuredJsonDoc ?? null,
    published_at:        new Date().toISOString(),
  };
}

// ── Core dispatcher ───────────────────────────────────────────────────────────

/**
 * Unified platform publish dispatcher.
 * Returns a PublishResult — never throws.
 */
export async function publishPayload(
  platform: PlatformId,
  bundle: GeneratedBundle,
  config: PlatformConfig,
  campaignId: string | null,
): Promise<PublishResult> {
  const baseResult: Omit<PublishResult, 'success' | 'statusCode' | 'message' | 'responseId'> = { platform };

  try {
    let url    = '';
    let body: Record<string, unknown> = {};
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Authorization:  `Bearer ${config.token}`,
    };

    switch (platform) {
      case 'X':
        url  = 'https://api.twitter.com/2/tweets';
        body = buildXPayload(bundle);
        break;

      case 'LinkedIn':
        url  = 'https://api.linkedin.com/v2/ugcPosts';
        body = buildLinkedInPayload(bundle, config.linkedInUrn ?? '');
        headers['LinkedIn-Version'] = '202304';
        break;

      case 'WordPress': {
        const base = (config.wpBaseUrl ?? '').replace(/\/$/, '');
        if (!base) throw new Error('WordPress Base URL is required.');
        url  = `${base}/wp-json/wp/v2/posts`;
        body = buildWordPressPayload(bundle);
        break;
      }

      case 'Instagram': {
        const igUser = config.igUserId;
        if (!igUser) throw new Error('Instagram User ID is required.');
        url  = `https://graph.facebook.com/v19.0/${igUser}/media`;
        body = buildInstagramPayload(bundle);
        break;
      }

      case 'Facebook': {
        const pageId = config.pageId;
        if (!pageId) throw new Error('Facebook Page ID is required.');
        url  = `https://graph.facebook.com/v19.0/${pageId}/feed`;
        body = buildFacebookPayload(bundle);
        break;
      }

      case 'YouTube':
        url  = 'https://www.googleapis.com/youtube/v3/videos?part=snippet,status';
        body = buildYouTubePayload(bundle);
        break;

      case 'MGB': {
        const mgbBase = (config.mgbBaseUrl ?? '').replace(/\/$/, '');
        if (!mgbBase) throw new Error('MGB Base URL is required.');
        url  = `${mgbBase}/api/posts`;
        body = buildMGBPayload(bundle, config.mgbAuthToken ?? config.token);
        if (config.mgbAuthToken) {
          headers['X-Compliance-Token'] = config.mgbAuthToken;
        }
        break;
      }

      default:
        throw new Error(`Unknown platform: ${platform}`);
    }

    const res = await fetch(url, {
      method:  'POST',
      headers,
      body:    JSON.stringify(body),
    });

    const responseText = await res.text().catch(() => '');
    let responseId: string | undefined;
    try {
      const json = JSON.parse(responseText) as Record<string, unknown>;
      const jsonData = json?.data as Record<string, unknown> | undefined;
      const jsonSys  = json?.sys  as Record<string, unknown> | undefined;
      responseId = (json?.id ?? jsonData?.id ?? jsonSys?.id) as string | undefined;
    } catch { /* plain text */ }

    // Log event to Supabase analytics
    await trackEvent({
      campaign_id: campaignId,
      platform,
      event_type:  res.ok ? 'publish_success' : 'publish_error',
      metadata:    { status: res.status, responseId },
    });

    if (campaignId && res.ok) {
      await updateCampaignStatus(campaignId, 'published');
    }

    return {
      ...baseResult,
      success:    res.ok,
      statusCode: res.status,
      message:    res.ok
        ? `✓ Published — ${responseId ? `ID: ${responseId}` : `HTTP ${res.status}`}`
        : `✗ HTTP ${res.status}: ${responseText.slice(0, 180)}`,
      responseId,
    };
  } catch (err: unknown) {
    await trackEvent({
      campaign_id: campaignId,
      platform,
      event_type:  'publish_error',
      metadata:    { error: String(err) },
    });
    return {
      ...baseResult,
      success:    false,
      statusCode: 0,
      message:    `✗ ${err instanceof Error ? err.message : String(err)}`,
    };
  }
}

/**
 * Publish to all enabled platforms concurrently.
 * Creates a campaign record in Supabase before dispatching.
 */
export async function publishToAllPlatforms(
  bundle: GeneratedBundle,
  configs: PlatformConfigs,
): Promise<PublishResult[]> {
  const enabledPlatforms = (Object.keys(configs) as PlatformId[]).filter(
    p => configs[p].enabled,
  );

  if (enabledPlatforms.length === 0) {
    return [];
  }

  // Create campaign row
  const campaignId = await createCampaign(
    {
      title:   bundle.blogPost.title,
      trendId: bundle.trendId,
      type:    'article',
    },
    enabledPlatforms,
  );

  // Dispatch all platforms concurrently
  const results = await Promise.all(
    enabledPlatforms.map(platform =>
      publishPayload(platform, bundle, configs[platform], campaignId),
    ),
  );

  return results;
}
